import { useState } from "react";
import { useAdminStats } from "@/hooks/useAdminStats";
import type { StatsPeriod } from "@/services/admin/adminStatsService";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { BarChart3, MessageSquare, ClipboardCheck, Gavel } from "lucide-react";
import { AdminStatsPopup } from "./AdminStatsPopup";
import { OnlineUsersDropdown } from "@/admin/components/OnlineUsersDropdown";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useCurrentAdminPermissions } from "@/hooks/useCurrentAdminPermissions";

const DASHBOARD_PERIOD: StatsPeriod = "1d";

export function AdminStatsBar() {
  const [period, setPeriod] = useState<StatsPeriod>(DASHBOARD_PERIOD);
  const [popupOpen, setPopupOpen] = useState(false);
  const { stats, isLoading, error } = useAdminStats(DASHBOARD_PERIOD);
  const { theme } = useTheme();
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const s = (adminLabels(language).stats ?? {}) as Record<string, string>;
  const isDark = theme === "dark";
  const { canViewBidsOverview, canViewDashboardOverview } =
    useCurrentAdminPermissions();

  if (error) {
    return null;
  }

  return (
    <>
      <div
        className={cn(
          "w-full sm:w-[85%] mx-auto rounded-lg sm:rounded-xl border shadow-sm",
          "bg-card text-card-foreground",
          isDark ? "border-border/50" : "border-border",
          isNative && "native-admin-stats-bar",
        )}
      >
        <div className="flex flex-nowrap items-center justify-between gap-1 sm:gap-2 px-2 py-1 sm:gap-3 sm:px-4 sm:py-2 overflow-x-auto">
          <div className="shrink-0">
            <OnlineUsersDropdown compact />
          </div>
          <div className="flex-1 flex items-center justify-center gap-1 sm:gap-2 md:gap-8 min-w-0">
            {isLoading ? (
              <span className="text-[10px] sm:text-xs text-muted-foreground">
                {s.loading ?? "Laddar..."}
              </span>
            ) : stats ? (
              <>
                <div className="flex items-center gap-0.5 sm:gap-2 shrink-0 whitespace-nowrap">
                  <MessageSquare className="h-2.5 w-2.5 sm:h-4 sm:w-4 text-muted-foreground shrink-0" />
                  <span className="text-[9px] sm:text-sm text-muted-foreground">
                    {s.leads ?? "Leads"}
                  </span>
                  <span className="font-semibold tabular-nums text-[9px] sm:text-sm">
                    {stats.leads.incomingInPeriod}
                  </span>
                </div>
                <div className="flex items-center gap-0.5 sm:gap-2 shrink-0 whitespace-nowrap">
                  <ClipboardCheck className="h-2.5 w-2.5 sm:h-4 sm:w-4 text-muted-foreground shrink-0" />
                  <span className="text-[9px] sm:text-sm text-muted-foreground">
                    {s.tests ?? "Tester"}
                  </span>
                  <span className="font-semibold tabular-nums text-[9px] sm:text-sm">
                    {stats.carTests.incomingInPeriod}
                  </span>
                </div>
                <div className="flex items-center gap-0.5 sm:gap-2 shrink-0 whitespace-nowrap">
                  <Gavel className="h-2.5 w-2.5 sm:h-4 sm:w-4 text-muted-foreground shrink-0" />
                  <span className="text-[9px] sm:text-sm text-muted-foreground">
                    {s.bids ?? "Bud"}
                  </span>
                  <span className="font-semibold tabular-nums text-[9px] sm:text-sm">
                    {stats.bids.bidCountInPeriod}
                  </span>
                </div>
              </>
            ) : null}
          </div>
          {canViewDashboardOverview && (
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1 text-[9px] sm:h-8 sm:px-2 sm:text-xs shrink-0 whitespace-nowrap"
              onClick={() => setPopupOpen(true)}
            >
              <BarChart3 className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
              <span className="sm:hidden">{s.more ?? "Mer"}</span>
              <span className="hidden sm:inline">
                {s.showMore ?? "Visa mer"}
              </span>
            </Button>
          )}
        </div>
      </div>

      {canViewDashboardOverview && (
        <AdminStatsPopup
          open={popupOpen}
          onOpenChange={setPopupOpen}
          period={period}
          onPeriodChange={setPeriod}
          initialStats={stats ?? undefined}
          canViewBidsOverview={canViewBidsOverview}
        />
      )}
    </>
  );
}
